import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { spacing } from '@vacationtracker/shared/constants/ThemeSetting'

import { useAppSelector } from '../../../../../store/hooks'
import { selectAuthUserSlice } from '../../../../../store/auth-user-slice'
import { Button } from '../../../../../design-system/Button'
import { EmptyState } from '../../../../../design-system/EmptyState'
import { FilterTrigger } from '../../../../../design-system/FilterTrigger'
import { Stack } from '../../../../../design-system/Stack'
import { Search } from '../../../../../components/search'
import IntlMessages from '../../../../../util/IntlMessages'
import { usePermissions } from '../../../../Users/UsersPage/hooks/usePermissions'
import { useUsersReferenceData } from '../../../../Users/UsersPage/hooks/useUsersReferenceData'
import { useUsersInfinite } from '../../../../Users/UsersPage/hooks/useUsersInfinite'
import { UsersTableSection } from '../../../../Users/UsersPage/components/UsersTableSection'
import { buildDepartmentOptions } from '../../../../Users/UsersPage/columns/departmentOptions'
import { COLUMN_IDS, PROFILE_DEFAULT_ORDER } from '../../../../Users/UsersPage/columns/columnIds'
import { AddUsersFromElsewhereModal } from './AddUsersFromElsewhereModal'

export interface ILocationUsersProps {
  locationId: string
  locationName: string
  /** False while another tab is showing: the pane stays mounted, so the bulk bar has to stand down. */
  active: boolean
}

// The tab has no column-config control, so the arrangement is fixed rather than read from the
// Users page's stored one - otherwise an admin who had reordered /app/users would see columns here
// that this screen never offers a way to hide again. Name, select and actions are pinned by the
// section itself, and the location column is dropped by locationScope.
const VISIBLE_COLUMNS: string[] = [COLUMN_IDS.team, COLUMN_IDS.role, COLUMN_IDS.status]

const COLUMNS = {
  columnOrder: VISIBLE_COLUMNS,
  // Visibility is a sparse map of false entries: the column builder defines every profile column
  // regardless of the order, and one it omits here renders. So each unwanted column has to be named,
  // and deriving the list means a newly added profile column stays out of this tab by default.
  columnVisibility: Object.fromEntries(PROFILE_DEFAULT_ORDER.filter((id) => !VISIBLE_COLUMNS.includes(id)).map((id) => [id, false])),
}

export const LocationUsers = ({ locationId, locationName, active }: ILocationUsersProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { amIAdmin, isFullAdmin, isLocationAdmin, managedLocationIds } = usePermissions()

  const [status, setStatus] = useState('ACTIVE')
  const [name, setName] = useState('')
  const [teamIds, setTeamIds] = useState<string[]>([])
  const [isAdding, setIsAdding] = useState(false)

  const canManage = !isLocationAdmin || managedLocationIds.includes(locationId)

  const filters = useMemo(() => ({ status, name, locationIds: [locationId], teamIds, labelIds: [] }), [status, name, locationId, teamIds])

  const reference = useUsersReferenceData({ approverScoped: false, authUserId: authUser.id })
  const departmentOptions = useMemo(() => buildDepartmentOptions(reference.teams), [reference.teams])
  const namesById = useMemo(
    () => ({
      locations: Object.fromEntries(reference.locations.map((location) => [location.id, location.name])),
      teams: Object.fromEntries(reference.teams.map((team) => [team.id, team.name])),
    }),
    [reference.locations, reference.teams]
  )

  // Same memoized filters as the table below, so this shares its react-query entry rather than
  // issuing a second request.
  const { users, scannedCount } = useUsersInfinite(filters)

  if (!canManage) {
    return <EmptyState description={formatMessage({ id: 'locationGeneral.lockedNotYours' })} />
  }

  const statusOptions = [
    { value: 'ACTIVE', label: formatMessage({ id: 'app.active' }) },
    ...(amIAdmin ? [{ value: 'INACTIVE', label: formatMessage({ id: 'app.inactive' }) }] : []),
  ]
  const departmentsLabel = formatMessage({ id: 'app.departments' })

  return (
    <div>
      <div className="vt-users-count-line">
        <IntlMessages id="users.table.usersCount" values={{ count: scannedCount ?? users.length }} />
      </div>

      <Stack direction="horizontal" justify="space-between" align="center" gap="xs" wrap style={{ marginBottom: spacing.base }}>
        <Stack direction="horizontal" align="center" gap="xs" wrap>
          <Search onSearch={setName} placeholder={formatMessage({ id: 'users.search' })} />
          <div className="vt-users-filterbar">
            <FilterTrigger
              config={{ id: 'status', label: formatMessage({ id: 'app.status' }), options: statusOptions, multiple: false }}
              value={[status]}
              // Status always has a value, so clearing means "back to the default" rather than none -
              // the × would promise something it cannot do. Matches the Users page filter bar.
              clearable={false}
              onChange={(values) => setStatus(values[0] || 'ACTIVE')}
            />
            <FilterTrigger
              config={{
                id: 'departments',
                label: departmentsLabel,
                options: departmentOptions.map((option) => ({ value: option.id, label: option.name, indentLevel: option.level })),
              }}
              value={teamIds}
              onChange={setTeamIds}
              clearLabel={formatMessage({ id: 'users.table.clearFilter' }, { filter: departmentsLabel })}
            />
          </div>
        </Stack>
        {isFullAdmin && (
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <IntlMessages id="users.addUsers" />
          </Button>
        )}
      </Stack>

      <UsersTableSection
        filters={filters}
        reference={reference}
        columnPrefs={COLUMNS}
        locationScope={{ locationId, moveLabelId: 'locationGeneral.moveTo' }}
        energyEnabled={false}
        active={active}
      />

      {isAdding && <AddUsersFromElsewhereModal locationId={locationId} locationName={locationName} namesById={namesById} onClose={() => setIsAdding(false)} />}
    </div>
  )
}
